import appendCartItems from '../globals/append-cart-items';

if (!customElements.get('cart-drawer')) {
  customElements.define(
    'cart-drawer',

    class CartDrawer extends HTMLElement {
      constructor() {
        super();

        this.cartDrawerIsOpen = false;

        this.cartDrawerClose = this.querySelectorAll('[data-cart-drawer-close]');
        this.cartDrawerInner = this.querySelector('[data-cart-drawer-inner]');
        this.openCartDrawer = this.openCartDrawer.bind(this);
        this.closeCartDrawer = this.closeCartDrawer.bind(this);
        this.toggleCartDrawer = this.toggleCartDrawer.bind(this);
        this.openCartDrawerOnProductAdded = this.openCartDrawerOnProductAdded.bind(this);
        this.openCartDrawerOnSelect = this.openCartDrawerOnSelect.bind(this);
        this.closeCartDrawerOnDeselect = this.closeCartDrawerOnDeselect.bind(this);
        this.cartDrawerSection = this.closest('.shopify-section');
        this.a11y = window.theme.a11y;
        this.checkoutButton = this.querySelector('.cart__checkout');

        this.closeCartEvents();
      }

      connectedCallback() {
        const drawerSection = this.closest('.shopify-section');

        /* Prevent duplicated cart drawers */
        if (window.theme.hasCartDrawer) {
          if (!window.Shopify.designMode) {
            drawerSection.remove();
            return;
          } else {
            const errorMessage = document.createElement('div');
            errorMessage.classList.add('drawer-editor-error');
            errorMessage.innerText = 'Cart drawer section already exists.';

            if (!this.querySelector(`.${'drawer-editor-error'}`)) {
              this.querySelector('[data-cart-drawer-inner]').append(errorMessage);
            }

            this.classList.add('drawer--duplicate');
          }
        }

        this.checkUrl();

        window.theme.hasCartDrawer = true;

        this.addEventListener('theme:cart-drawer:show', this.openCartDrawer);
        document.addEventListener('theme:cart-drawer:toggle', this.toggleCartDrawer);
        document.addEventListener('theme:quick-add:open', this.closeCartDrawer);
        document.addEventListener('theme:product:added', this.openCartDrawerOnProductAdded);
        document.addEventListener('shopify:block:select', this.openCartDrawerOnSelect);
        document.addEventListener('shopify:section:select', this.openCartDrawerOnSelect);
        document.addEventListener('shopify:section:deselect', this.closeCartDrawerOnDeselect);

        // Fix cart price issues after returning from the checkout page without page reload
        if (this.checkoutButton) {
          this.checkoutButton.addEventListener('click', () => {
            sessionStorage.setItem('cameFromCheckout', 'true');
          });
        }

        window.addEventListener('pageshow', function (event) {
          if (event.persisted && sessionStorage.getItem('cameFromCheckout') === 'true') {
            window.location.reload();
          }
          sessionStorage.removeItem('cameFromCheckout');
        });
      }

      disconnectedCallback() {
        document.removeEventListener('theme:product:added', this.openCartDrawerOnProductAdded);
        document.removeEventListener('theme:cart-drawer:toggle', this.toggleCartDrawer);
        document.removeEventListener('theme:quick-add:open', this.closeCartDrawer);
        document.removeEventListener('shopify:block:select', this.openCartDrawerOnSelect);
        document.removeEventListener('shopify:section:select', this.openCartDrawerOnSelect);
        document.removeEventListener('shopify:section:deselect', this.closeCartDrawerOnDeselect);

        if (document.querySelectorAll('cart-drawer').length <= 1) {
          window.theme.hasCartDrawer = false;
        }

        appendCartItems();
      }

      /**
       * Open cart drawer when redirect from cart page
       *
       * @return  {Void}
       */
      checkUrl() {
        const params = new URLSearchParams(window.location.search);
        if (params.has('cart')) {
          params.delete('cart');
          this.openCartDrawer( true);
          const deletePathName = params.toString().length ? `?${params.toString()}` : '' ;
          const newUrl = `${window.location.origin}${window.location.pathname}${deletePathName}`;
          window.history.pushState(null, null, newUrl);
        }
      }

      /**
       * Open cart drawer when product is added to cart
       *
       * @return  {Void}
       */
      openCartDrawerOnProductAdded() {
        if (theme.settings.cartType !== 'drawer') return;
        if (!this.cartDrawerIsOpen) {
          this.openCartDrawer();
        }
      }

      /**
       * Open cart drawer on block or section select
       *
       * @return  {Void}
       */
      openCartDrawerOnSelect(e) {
        const cartDrawerSection = e.target.querySelector('.shopify-section') || e.target.closest('.shopify-section') || e.target;

        if (cartDrawerSection === this.cartDrawerSection) {
          this.openCartDrawer(true);
        }
      }

      /**
       * Close cart drawer on section deselect
       *
       * @return  {Void}
       */
      closeCartDrawerOnDeselect() {
        if (this.cartDrawerIsOpen) {
          this.closeCartDrawer();
        }
      }

      /**
       * Open cart drawer and add class on body
       *
       * @return  {Void}
       */

      openCartDrawer(forceOpen = false) {
        if (!forceOpen && this.classList.contains('drawer--duplicate')) return;

        this.cartDrawerIsOpen = true;
        this.onBodyClickEvent = this.onBodyClickEvent || this.onBodyClick.bind(this);
        document.body.addEventListener('click', this.onBodyClickEvent);

        document.dispatchEvent(
          new CustomEvent('theme:cart-drawer:open', {
            detail: {
              target: this,
            },
            bubbles: true,
          })
        );
        document.dispatchEvent(new CustomEvent('theme:scroll:lock', {bubbles: true}));

        this.classList.add('is-open');

        // Load images instantly when open Cart drawer
        for (const img of this.querySelectorAll('img[loading="lazy"]')) img.removeAttribute('loading');

        // Observe Additional Checkout Buttons
        this.observeAdditionalCheckoutButtons();

        window.theme.waitForAnimationEnd(this.cartDrawerInner).then(() => {
          this.a11y.trapFocus(this, {
            elementToFocus: this.querySelector('[data-cart-drawer-close]'),
          });
        });
      }

      /**
       * Close cart drawer and remove class on body
       *
       * @return  {Void}
       */

      closeCartDrawer() {
        if (!this.classList.contains('is-open')) return;

        this.classList.add('is-closing');
        this.classList.remove('is-open');

        this.cartDrawerIsOpen = false;

        document.dispatchEvent(
          new CustomEvent('theme:cart-drawer:close', {
            bubbles: true,
          })
        );

        this.a11y.removeTrapFocus();
        this.a11y.autoFocusLastElement();

        document.body.removeEventListener('click', this.onBodyClickEvent);
        document.dispatchEvent(new CustomEvent('theme:scroll:unlock', {bubbles: true}));

        window.theme.waitForAnimationEnd(this.cartDrawerInner).then(() => {
          this.classList.remove('is-closing');
        });

      }

      /**
       * Toggle cart drawer
       *
       * @return  {Void}
       */

      toggleCartDrawer() {
        if (!this.cartDrawerIsOpen) {
          this.openCartDrawer();
        } else {
          this.closeCartDrawer();
        }
      }

      /**
       * Event click to element to close cart drawer
       *
       * @return  {Void}
       */

      closeCartEvents() {
        this.cartDrawerClose.forEach((btn) => {
          btn.addEventListener('click', (e) => {
              e.preventDefault();
              this.closeCartDrawer();
            });
        })

        this.addEventListener('keyup', (e) => {
          if (e.code === 'Escape') {
            this.closeCartDrawer();
          }
        });
      }

      onBodyClick(e) {
        if (e.target.hasAttribute('data-drawer-underlay')) this.closeCartDrawer();
      }

      observeAdditionalCheckoutButtons() {
        // identify an element to observe
        const additionalCheckoutButtons = this.querySelector('.additional-checkout-buttons');
        if (additionalCheckoutButtons) {
          // create a new instance of `MutationObserver` named `observer`,
          // passing it a callback function
          const observer = new MutationObserver(() => {
            this.a11y.trapFocus(this, {
              elementToFocus: this.querySelector('[data-cart-drawer-close]'),
            });
            observer.disconnect();
          });

          // call `observe()` on that MutationObserver instance,
          // passing it the element to observe, and the options object
          observer.observe(additionalCheckoutButtons, {subtree: true, childList: true});
        }
      }
    }
  );
}


if (!customElements.get('add-membership')) {
  customElements.define(
    'add-membership',

    class AddMembership extends HTMLElement {
      constructor() {
        super();

        this.fieldset = this.querySelector('fieldset');
        this.drawerSection = this.closest('.shopify-section');
        this.cartDrawer = this.closest('cart-drawer') || document.querySelector('cart-drawer');
        this.memberDrawer = this.drawerSection.querySelector('[data-membership-drawer]');
        this.addMemberButtons = document.querySelectorAll('[data-add-membership]') || [];
        this.infoOpener = this.querySelector('[data-member-info-opener]');
        this.infoPopup = this.querySelector('[data-info-popup]');
        this.membershipDrawerClose = this.drawerSection.querySelectorAll('[data-membership-drawer-close]');
        this.memberDrawerOverlay = this.memberDrawer?.querySelector('[data-drawer-underlay]');
      }

      connectedCallback() {
        if (this.infoOpener) {
          this.infoOpener.addEventListener('click', (e) => {
            e.preventDefault();
            this.infoPopup.popupOpen();
          });
        }

        if (this.fieldset) {
          this.fieldset.addEventListener('change', (e) => {
            const value = e.target.value;

            if (value === 'member') {
              if (this.memberDrawer) {
                this.memberDrawer.showDrawer();
              } else {
                const submitButton = this.querySelector('[data-add-membership]');
                this.addMembership(submitButton);
              }
            } else {
              const membershipItem = this.drawerSection.querySelector('[data-item-membership]');
              const cartItemsElem = this.drawerSection.querySelector('cart-items');
              if (!membershipItem || !cartItemsElem) return;

              const membershipItemDelete = membershipItem.querySelector('[data-item-remove]');
              cartItemsElem.updateCart(
                {
                  id: membershipItemDelete.dataset.id,
                  quantity: 0,
                },
                membershipItem
              );
            }
          });
        }

        this.addMemberButtons.forEach(btn => { btn.addEventListener('click', (e) => {
            e.preventDefault();
            this.addMembership(btn);
          });
        });

        if (this.membershipDrawerClose) {
          this.membershipDrawerClose.forEach((btn) => {
            btn.addEventListener('click', (e) => {
              this.querySelector('#cart-membership-switcher-delete').checked = true;
            });
          });
        }

        if (this.memberDrawerOverlay) {
          this.memberDrawerOverlay.addEventListener('click', (e) => {
            this.querySelector('#cart-membership-switcher-delete').checked = true;
          });
        }
      }

      addMembership(btn) {
        if (!btn || window.theme.membership.inCart || theme.membership.isMember) {
          this.cartDrawer.openCartDrawer();
          const membershipPopup = document.querySelector('popup-component.product_membership__popup');
          membershipPopup?.popupClose();
          return;
        }

        document.dispatchEvent(
          new CustomEvent('theme:cart:add', {
            detail: {
              button: btn
            }
          })
        );

        setTimeout(() => {
          this.memberDrawer?.hideDrawer();
        }, 1000)
      }
    }
  );
}
